"use client";

/**
 * Enrollment-preview step card (Figma `10356:10916` / `10356:11016`).
 *
 * One card per pinned step, rendered against the selected candidate. It
 * shows the FINAL, per-candidate content that will actually be sent —
 * variables already resolved to their values, AI prompts already
 * generated, NO preview colors (those are a step-editor affordance only).
 *
 * Header (Figma `…Score`):
 *   - circular step number,
 *   - a type pill (icon + label, e.g. "Email"),
 *   - for the FIRST step, the absolute first-send ETA line
 *     ("This will execute on August 4, 2025 at 01:21 PM Eastern Time").
 *
 * Body:
 *   - email steps: an editable subject input + an editable rich body
 *     (reusing the sequences `StepEditor`, formatting toolbar only — no
 *     variable/snippet/AI insertion, since this is final copy).
 *   - Social message / task steps: an editable rich note.
 *   - LinkedIn connection request: an invitation-note textarea (≤300).
 *   - Social profile view: no editable content.
 *
 * Editing is the override mechanism: every change autosaves the full
 * per-step override (content docs + the candidate's selected
 * `email_to_address`) so the recruiter's edits become the exact frozen
 * content for this enrollment. The card is the SOLE writer of its step's
 * override, so changing the recipient address never clobbers a content
 * edit and vice-versa.
 */

import * as React from "react";

import { Icon } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { StepEditor, type ProseMirrorDoc } from "@/features/sequences/components/editors/step-editor";
import {
  STEP_TYPE_META,
  type StepType,
} from "@/features/sequences/lib/step-type-meta";
import type {
  SequenceStepResponse,
  ValidationIssueResponse,
} from "@/lib/api/model";
import { cn } from "@/lib/utils";

import { useDebouncedOverride } from "../../hooks/use-debounced-override";
import { finalHtmlToDoc, finalTextToDoc } from "../../lib/final-content";
import { ISSUE_META } from "../../lib/issue-meta";
import { docToPlainText, plainTextToDoc } from "@/lib/prosemirror/plain-text";
import type {
  EnrollmentPreviewPatchRequestSetOverrides,
  PreviewStepRender,
  StepOverridePayload,
} from "../../lib/types";

export interface EnrollStepCardProps {
  jobId: string;
  sequenceId: string;
  jobCandidateId: string;
  step: SequenceStepResponse;
  stepNumber: number;
  /** Pre-rendered final content for this (candidate × step). */
  cell: PreviewStepRender | undefined;
  /** Validation issues scoped to this step. */
  issues: ValidationIssueResponse[];
  /** First step only: absolute "will execute on …" ETA line. */
  executeLabel?: string | null;
  /** For a reply step: the step number whose thread it replies into (the
   * preceding new-thread email its subject is derived from). */
  replySourceStepNumber?: number | null;
  /** Candidate's chosen recipient — folded into every email override. */
  selectedEmail?: string | null;
  patchPreview: (data: {
    set_overrides?: EnrollmentPreviewPatchRequestSetOverrides;
    request_seq?: number | null;
  }) => Promise<unknown>;
}

function isEmail(type: string): boolean {
  return type === "automatic_email" || type === "manual_email";
}

export function EnrollStepCard({
  jobId,
  sequenceId,
  jobCandidateId,
  step,
  stepNumber,
  cell,
  issues,
  replySourceStepNumber,
  executeLabel,
  selectedEmail,
  patchPreview,
}: EnrollStepCardProps) {
  const meta = STEP_TYPE_META[step.type as StepType] ?? STEP_TYPE_META.custom_task;
  const stepType = step.type as StepType;
  const email = isEmail(stepType);
  const isProfileView = stepType === "linkedin_profile_view";
  const isInvitation = stepType === "linkedin_connection_request";
  // A reply derives its subject from the thread it replies to (the prior
  // email), so there's no editable subject — show a derived note instead.
  const isReply = email && step.email_thread_mode === "reply";

  // Initial seeds, captured once per (candidate × step). The card key
  // (set by the parent) remounts the card on candidate switch so these
  // re-seed from the freshest rendered cell without resetting the cursor
  // mid-edit.
  const initialSubject = cell?.email_subject ?? "";
  const initialInvitation = cell?.linkedin_message_text ?? "";

  const [subject, setSubject] = React.useState(initialSubject);
  const [bodyDoc, setBodyDoc] = React.useState<ProseMirrorDoc>(() => {
    // Seed from the rendered FINAL content. Email seeds from HTML so the
    // paragraph structure + bold/italic survive exactly (and the
    // preview-only variable/AI colors are dropped); other channels seed
    // from the `\n\n`/`\n` plain-text convention. This is what fixes the
    // "blank lines multiplied" bug — naive `\n` splitting is gone.
    if (email) {
      return finalHtmlToDoc(cell?.email_body_html) as unknown as ProseMirrorDoc;
    }
    const text = cell?.linkedin_message_text ?? cell?.task_note_text ?? "";
    return finalTextToDoc(text) as unknown as ProseMirrorDoc;
  });
  const [invitation, setInvitation] = React.useState(initialInvitation);

  const autosave = useDebouncedOverride({
    jobCandidateId,
    stepId: step.id,
    patchPreview,
  });

  // Assemble the COMPLETE override for this step from current state plus
  // the candidate's selected recipient (email steps only). Returns null
  // for profile-view (no editable content).
  const buildPayload = React.useCallback(
    (next: {
      subject?: string;
      bodyDoc?: ProseMirrorDoc;
      invitation?: string;
    }): StepOverridePayload | null => {
      const subj = next.subject ?? subject;
      const body = next.bodyDoc ?? bodyDoc;
      const invite = next.invitation ?? invitation;
      const toAddress = selectedEmail ?? null;
      switch (stepType) {
        case "automatic_email":
        case "manual_email":
          return {
            type: stepType,
            // Replies derive their subject from the thread — never send one.
            email_subject_doc:
              isReply || subj.trim().length === 0 ? null : plainTextToDoc(subj),
            email_body_doc: body as never,
            ...(toAddress ? { email_to_address: toAddress } : {}),
          };
        case "linkedin_message":
          return { type: "linkedin_message", linkedin_message_doc: body as never };
        case "linkedin_connection_request":
          return {
            type: "linkedin_connection_request",
            linkedin_invitation_note: invite.trim() || null,
          };
        case "phone_call":
        case "text_message":
        case "custom_task":
          return { type: stepType, task_note_doc: body as never };
        default:
          return null;
      }
    },
    [stepType, subject, bodyDoc, invitation, selectedEmail, isReply],
  );

  // When the recruiter changes the recipient at the top of the pane, fold
  // the new address into THIS email step's override (alongside the current
  // content) so the chosen address is frozen without clobbering edits.
  //
  // Unlike content edits (debounced so typing doesn't PATCH per keystroke),
  // a recipient change is a single deliberate action whose effect must be
  // immediate: it has to persist BEFORE the recruiter clicks Enroll (else
  // the commit falls back to the waterfall pick and the wrong address is
  // recorded/sent) and it should clear/raise the "no email" issue right
  // away. So we save AND flush — bypassing the debounce window.
  const lastEmailRef = React.useRef<string | null | undefined>(selectedEmail);
  React.useEffect(() => {
    if (!email) return;
    if (lastEmailRef.current === selectedEmail) return;
    lastEmailRef.current = selectedEmail;
    const payload = buildPayload({});
    if (payload) {
      autosave.save(payload);
      void autosave.flush();
    }
    // Only react to recipient changes here; content edits save via their
    // own handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmail, email]);

  const stepBlocked = issues.some((i) => i.severity === "fix");
  const stepWarning = !stepBlocked && issues.some((i) => i.severity === "warning");

  // The signature + unsubscribe link aren't rendered into the editable preview
  // body (they're appended at send time), so surface a note on email steps so
  // the recruiter knows they'll be added to the outgoing message.
  const appendParts: string[] = [];
  if (step.email_include_sender_signature) appendParts.push("your signature");
  if (step.email_include_unsubscribe_link)
    appendParts.push("an unsubscribe link");
  const appendNote =
    email && appendParts.length > 0
      ? `When sent, ${appendParts.join(" and ")} will be appended to this email.`
      : null;

  return (
    <article
      data-step-id={step.id}
      className="flex w-full flex-col"
    >
      {/* Header */}
      <div
        className={cn(
          "flex flex-col gap-1 rounded-t-xl border border-b-0 bg-[#252526] px-5 py-4 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
          stepBlocked
            ? "border-destructive/40"
            : stepWarning
              ? "border-amber-500/40"
              : "border-white/10",
        )}
      >
        <div className="flex items-center gap-2">
          <span className="flex size-[26px] items-center justify-center rounded-full bg-white/[0.04] text-sm font-medium text-heading shadow-[inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]">
            {stepNumber}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.04] px-2 py-1 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
            <Icon name={meta.icon} size={16} className="text-foreground opacity-80" />
            <span className="text-sm font-medium text-heading">{meta.label}</span>
          </span>
        </div>
        {executeLabel ? (
          <p className="text-base text-muted-foreground">{executeLabel}</p>
        ) : null}
      </div>

      {/* Body */}
      <div
        className={cn(
          "flex flex-col gap-6 rounded-b-xl border border-t-0 bg-[#1f1f20] p-5",
          stepBlocked
            ? "border-destructive/40"
            : stepWarning
              ? "border-amber-500/40"
              : "border-white/10",
        )}
      >
        {email ? (
          <div className="flex items-center gap-2">
            <label
              htmlFor={`enroll-subject-${step.id}`}
              className="shrink-0 text-sm font-medium text-heading"
            >
              Email Subject
            </label>
            {isReply ? (
              <span className="flex flex-1 items-center gap-1.5 rounded-lg border border-white/15 bg-white/[0.02] px-3 py-2 text-sm text-muted-foreground">
                <Icon
                  name="IconArrowCornerDownLeft"
                  size={14}
                  className="shrink-0 opacity-70"
                />
                {replySourceStepNumber
                  ? `Derived from Step ${replySourceStepNumber}`
                  : "Derived from the previous step"}
              </span>
            ) : (
              <Input
                id={`enroll-subject-${step.id}`}
                type="text"
                variant="modal"
                value={subject}
                onChange={(e) => {
                  const next = e.target.value;
                  setSubject(next);
                  const payload = buildPayload({ subject: next });
                  if (payload) autosave.save(payload);
                }}
                onBlur={() => void autosave.flush()}
                placeholder="Subject line"
                className="flex-1"
              />
            )}
          </div>
        ) : null}

        {isProfileView ? (
          <p className="rounded-lg border border-border bg-white/[0.02] px-4 py-6 text-center text-sm text-muted-foreground">
            This step views the candidate&apos;s Social profile — there is no
            message content to send.
          </p>
        ) : isInvitation ? (
          <div className="flex flex-col gap-1">
            <textarea
              value={invitation}
              maxLength={300}
              onChange={(e) => {
                const next = e.target.value;
                setInvitation(next);
                const payload = buildPayload({ invitation: next });
                if (payload) autosave.save(payload);
              }}
              onBlur={() => void autosave.flush()}
              placeholder="Connection request note (optional, max 300 characters)"
              className="min-h-[120px] w-full rounded-xl border border-border bg-white/[0.02] px-3 py-2 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-primary/40"
            />
            <span className="self-end text-[11px] text-muted-foreground">
              {invitation.length}/300
            </span>
          </div>
        ) : (
          <div className="enroll-final-content min-h-[260px]">
            <StepEditor
              jobId={jobId}
              sequenceId={sequenceId}
              stepType={stepType}
              value={bodyDoc}
              onChange={(doc) => {
                setBodyDoc(doc);
                const payload = buildPayload({ bodyDoc: doc });
                if (payload) autosave.save(payload);
              }}
              enabledFeatures={{
                formatting: true,
                variables: false,
                snippets: false,
                aiPrompts: false,
                writeWithAi: false,
              }}
              placeholder="Message content"
              className="min-h-[260px]"
            />
          </div>
        )}

        {appendNote ? (
          <p className="flex items-start gap-2 rounded-md border border-white/10 bg-white/[0.02] px-2.5 py-1.5 text-xs text-muted-foreground">
            <Icon
              name="IconSignature"
              size={14}
              className="mt-0.5 shrink-0 opacity-70"
            />
            <span>{appendNote}</span>
          </p>
        ) : null}

        {cell?.status === "failed" ? (
          <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            Content failed to generate. Edit it above or remove this candidate
            before enrolling.
          </p>
        ) : null}

        {issues.length > 0 ? (
          <ul aria-label="Issues for this step" className="flex flex-col gap-1.5">
            {issues.map((issue, idx) => {
              const m = ISSUE_META[issue.code];
              const tone =
                issue.severity === "fix" ? "text-destructive" : "text-amber-500";
              return (
                <li
                  key={`${issue.code}-${idx}`}
                  className={cn(
                    "flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-xs",
                    issue.severity === "fix"
                      ? "border-destructive/30 bg-destructive/5"
                      : "border-amber-500/30 bg-amber-500/5",
                  )}
                >
                  <Icon
                    name={m?.icon ?? "IconExclamationCircle"}
                    size={14}
                    className={cn("mt-0.5", tone)}
                  />
                  <div className="flex-1">
                    <p className={cn("font-medium", tone)}>{m?.label ?? issue.code}</p>
                    <p className="text-muted-foreground">
                      {issue.message ?? m?.resolution ?? ""}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </article>
  );
}

/** Read the best-available plain text from an override doc (used by tests). */
export function overrideDocText(doc: unknown): string {
  return docToPlainText(doc as { type?: string; content?: unknown[] } | null);
}
